import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography, Box, CircularProgress, Alert, Tooltip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, FormControl, InputLabel,
  Select, MenuItem, Stack, Paper, Grid, Chip, Tabs, Tab, Switch, FormControlLabel
} from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import { ROLE_IDS } from "../config/roles.config";
import { useCurrentUser } from "../hooks/useCurrentUser";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';

const EMPTY_FORM = {
  id: '',
  customer_id: '',
  yubiq_code: '',
  cluster: '',
  tipologia_servizio: '',
  description: '',
  client_manager: '',
  service_manager: '',
  canone_mensile: '',
  importo_rinnovo: '',
  inizio_contratto: '',
  scadenza_contratto: '',
  stato_rinnovo: '',
  note: '',
};

const NOTE_MAX_LENGTH = 60;

const LOOKUP_TABS = [
  { field_key: 'contract_cluster', label: 'Cluster' },
  { field_key: 'contract_type', label: 'Type' },
  { field_key: 'contract_renewal_status', label: 'Renewal Status' },
];

const EMPTY_LOOKUP_FORM = {
  label: '',
  sort_order: 0,
};

/** Semantic contract_type value for Revenues Current Monthly Fee (must match backend seed). */
const MONTHLY_RATE_VALUE = 'Monthly Rate';

function lookupLabel(options, value) {
  if (!value) return '';
  const found = (options || []).find((o) => o.value === value);
  return found?.label || value;
}

export default function Contracts() {
  const { userId, userEmail, backendUser } = useCurrentUser();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });

  const [customers, setCustomers] = useState([]);
  const [serviceManagers, setServiceManagers] = useState([]);

  const [clusterOptions, setClusterOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [renewalOptions, setRenewalOptions] = useState([]);

  const [canManageLookups, setCanManageLookups] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Manage Contract Values dialog
  const [openManage, setOpenManage] = useState(false);
  const [manageTab, setManageTab] = useState(0);
  const [manageItems, setManageItems] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState(null);
  const [manageFormOpen, setManageFormOpen] = useState(false);
  const [manageEditing, setManageEditing] = useState(null);
  const [manageForm, setManageForm] = useState(EMPTY_LOOKUP_FORM);
  const [manageSaving, setManageSaving] = useState(false);

  const activeManageFieldKey = LOOKUP_TABS[manageTab]?.field_key;

  const aclQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (userEmail) params.set('email', userEmail);
    if (userId) params.set('user_id', String(userId));
    if (backendUser?.role_id != null) params.set('role_id', String(backendUser.role_id));
    return params.toString();
  }, [userEmail, userId, backendUser?.role_id]);

  const fetchActiveLookups = useCallback(async () => {
    try {
      const [clusterRes, typeRes, renewalRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=contract_cluster`),
        fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=contract_type`),
        fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=contract_renewal_status`),
      ]);
      const parse = async (res) => {
        if (!res.ok) return [];
        const data = await res.json();
        return data.items || [];
      };
      setClusterOptions(await parse(clusterRes));
      setTypeOptions(await parse(typeRes));
      setRenewalOptions(await parse(renewalRes));
    } catch {
      setClusterOptions([]);
      setTypeOptions([]);
      setRenewalOptions([]);
    }
  }, []);

  const fetchCanManage = useCallback(async () => {
    if (!userEmail && !userId) {
      setCanManageLookups(false);
      return;
    }
    try {
      const res = await fetch(`${API_ENDPOINTS.CONTRACT_LOOKUPS}?action=can-manage&${aclQuery}`);
      if (!res.ok) {
        setCanManageLookups(false);
        return;
      }
      const data = await res.json();
      setCanManageLookups(Boolean(data.can_manage));
    } catch {
      setCanManageLookups(false);
    }
  }, [aclQuery, userEmail, userId]);

  const fetchContracts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.CONTRACTS);
      if (!response.ok) throw new Error("Error fetching contracts");
      const data = await response.json();
      const contracts = data.contracts || [];
      setRows(contracts.map((row, idx) => ({ id: row.id || idx, ...row })));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CUSTOMERS);
      if (!response.ok) return;
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch {
      setCustomers([]);
    }
  };

  const fetchServiceManagers = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.USERS);
      if (!response.ok) return;
      const data = await response.json();
      const users = data.users || [];
      const sms = users.filter(
        (u) => u.role_id === ROLE_IDS.SERVICE_MANAGER || u?.role?.id === ROLE_IDS.SERVICE_MANAGER
      );
      setServiceManagers(sms);
    } catch {
      setServiceManagers([]);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchCustomers();
    fetchServiceManagers();
    fetchActiveLookups();
  }, [fetchActiveLookups]);

  useEffect(() => {
    fetchCanManage();
  }, [fetchCanManage]);

  const fetchManageItems = useCallback(async (fieldKey) => {
    if (!fieldKey) return;
    setManageLoading(true);
    setManageError(null);
    try {
      const res = await fetch(
        `${API_ENDPOINTS.CONTRACT_LOOKUPS}?field_key=${encodeURIComponent(fieldKey)}&include_inactive=true`
      );
      if (!res.ok) throw new Error('Unable to load lookup values');
      const data = await res.json();
      setManageItems(data.items || []);
    } catch (err) {
      setManageError(err.message);
      setManageItems([]);
    } finally {
      setManageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (openManage && activeManageFieldKey) {
      fetchManageItems(activeManageFieldKey);
    }
  }, [openManage, activeManageFieldKey, fetchManageItems]);

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton onClick={() => handleEditDialog(params.row)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDelete(params.row.id)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'customer_name', headerName: 'Customer', width: 160 },
    { field: 'yubiq_code', headerName: 'Yubiq Code', width: 130 },
    {
      field: 'cluster',
      headerName: 'Cluster',
      width: 110,
      valueGetter: (_v, row) => lookupLabel(clusterOptions, row.cluster) || row.cluster || '',
    },
    {
      field: 'tipologia_servizio',
      headerName: 'Type',
      width: 120,
      valueGetter: (_v, row) => lookupLabel(typeOptions, row.tipologia_servizio) || row.tipologia_servizio || '',
    },
    { field: 'client_manager', headerName: 'Client Manager', width: 150 },
    { field: 'service_manager', headerName: 'Service Manager', width: 150 },
    {
      field: 'canone_mensile',
      headerName: 'Current Monthly Fee',
      width: 150,
      valueFormatter: (value) => formatMoney(value),
    },
    {
      field: 'importo_rinnovo',
      headerName: 'Renewal Monthly Fee',
      width: 160,
      valueFormatter: (value) => formatMoney(value),
    },
    { field: 'inizio_contratto', headerName: 'Start Date', width: 120 },
    { field: 'scadenza_contratto', headerName: 'End Date', width: 120 },
    { field: 'note', headerName: 'Note', width: 200 },
  ];

  const handleOpenDialog = () => {
    setFormData(EMPTY_FORM);
    setEditingRow(null);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };

  const handleEditDialog = (row) => {
    setFormData({
      id: row.id || '',
      customer_id: row.customer_id || '',
      yubiq_code: row.yubiq_code || '',
      cluster: row.cluster || '',
      tipologia_servizio: row.tipologia_servizio || '',
      description: row.description || '',
      client_manager: row.client_manager || '',
      service_manager: row.service_manager || '',
      canone_mensile: row.canone_mensile != null ? String(row.canone_mensile) : '',
      importo_rinnovo: row.importo_rinnovo != null ? String(row.importo_rinnovo) : '',
      inizio_contratto: row.inizio_contratto || '',
      scadenza_contratto: row.scadenza_contratto || '',
      stato_rinnovo: row.stato_rinnovo || '',
      note: row.note || '',
    });
    setEditingRow(row);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'tipologia_servizio' && value !== MONTHLY_RATE_VALUE) {
        next.canone_mensile = '';
      }
      return next;
    });
  };

  const buildPayload = () => ({
    customer_id: parseInt(formData.customer_id, 10),
    yubiq_code: formData.yubiq_code.trim() || null,
    cluster: formData.cluster || null,
    tipologia_servizio: formData.tipologia_servizio || null,
    description: formData.description.trim() || null,
    client_manager: formData.client_manager.trim() || null,
    service_manager: formData.service_manager || null,
    canone_mensile:
      formData.tipologia_servizio === MONTHLY_RATE_VALUE && formData.canone_mensile !== ''
        ? parseFloat(formData.canone_mensile)
        : null,
    importo_rinnovo: formData.importo_rinnovo !== '' ? parseFloat(formData.importo_rinnovo) : null,
    inizio_contratto: formData.inizio_contratto || null,
    scadenza_contratto: formData.scadenza_contratto || null,
    stato_rinnovo: formData.stato_rinnovo || null,
    note: formData.note.trim() || null,
  });

  const handleSaveContract = async () => {
    setSaving(true);
    setFormError(null);

    const missing = [];
    if (!formData.customer_id) missing.push('Customer');
    if (!formData.yubiq_code.trim()) missing.push('Yubiq Code');
    if (!formData.cluster) missing.push('Cluster');
    if (!formData.tipologia_servizio) missing.push('Type');
    if (!formData.client_manager.trim()) missing.push('Client Manager');
    if (!formData.service_manager) missing.push('Service Manager');
    if (formData.tipologia_servizio === MONTHLY_RATE_VALUE && formData.canone_mensile === '') {
      missing.push('Current Monthly Fee');
    }
    if (formData.importo_rinnovo === '') missing.push('Renewal Monthly Fee');
    if (!formData.stato_rinnovo) missing.push('Renewal Status');
    if (!formData.inizio_contratto) missing.push('Contract Start Date');
    if (!formData.scadenza_contratto) missing.push('Contract End Date');
    if (!formData.description.trim()) missing.push('Description');
    if (!formData.note.trim()) missing.push('Note');

    if (missing.length) {
      setFormError(`Required fields missing: ${missing.join(', ')}.`);
      setSaving(false);
      return;
    }
    if (formData.scadenza_contratto < formData.inizio_contratto) {
      setFormError('Contract end date cannot be before contract start date.');
      setSaving(false);
      return;
    }

    try {
      const payload = buildPayload();
      const url = editingRow ? `${API_ENDPOINTS.CONTRACTS}?id=${editingRow.id}` : API_ENDPOINTS.CONTRACTS;
      const method = editingRow ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let message = `${response.status}: Request failed`;
        try {
          const err = await response.json();
          if (err?.error) message = err.error;
        } catch { /* ignore */ }
        throw new Error(message);
      }

      setOpenDialog(false);
      setEditingRow(null);
      await fetchContracts();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ENDPOINTS.CONTRACTS}?id=${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Error deleting contract');
      await fetchContracts();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openManageDialog = () => {
    setManageTab(0);
    setManageError(null);
    setOpenManage(true);
  };

  const closeManageDialog = () => {
    setOpenManage(false);
    setManageFormOpen(false);
    setManageEditing(null);
    setManageForm(EMPTY_LOOKUP_FORM);
    fetchActiveLookups();
  };

  const openManageCreate = () => {
    setManageEditing(null);
    setManageForm({ ...EMPTY_LOOKUP_FORM, sort_order: (manageItems.length + 1) * 10 });
    setManageFormOpen(true);
    setManageError(null);
  };

  const openManageEdit = (item) => {
    setManageEditing(item);
    setManageForm({
      label: item.label || '',
      sort_order: item.sort_order ?? 0,
    });
    setManageFormOpen(true);
    setManageError(null);
  };

  const aclBody = () => ({
    ...(userEmail ? { email: userEmail } : {}),
    ...(userId ? { user_id: userId } : {}),
    ...(backendUser?.role_id != null ? { role_id: backendUser.role_id } : {}),
  });

  const handleSaveLookup = async () => {
    setManageSaving(true);
    setManageError(null);
    try {
      if (!manageForm.label.trim()) {
        throw new Error('Label is required.');
      }

      let res;
      if (manageEditing) {
        res = await fetch(`${API_ENDPOINTS.CONTRACT_LOOKUPS}?id=${manageEditing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...aclBody(),
            label: manageForm.label.trim(),
            sort_order: Number(manageForm.sort_order) || 0,
          }),
        });
      } else {
        res = await fetch(API_ENDPOINTS.CONTRACT_LOOKUPS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...aclBody(),
            field_key: activeManageFieldKey,
            label: manageForm.label.trim(),
            sort_order: Number(manageForm.sort_order) || 0,
          }),
        });
      }

      if (!res.ok) {
        let message = 'Save failed';
        try {
          const err = await res.json();
          if (err?.error) message = err.error;
        } catch { /* ignore */ }
        throw new Error(message);
      }

      setManageFormOpen(false);
      setManageEditing(null);
      await fetchManageItems(activeManageFieldKey);
      await fetchActiveLookups();
    } catch (err) {
      setManageError(err.message);
    } finally {
      setManageSaving(false);
    }
  };

  const handleToggleLookupActive = async (item) => {
    setManageError(null);
    try {
      const res = await fetch(`${API_ENDPOINTS.CONTRACT_LOOKUPS}?id=${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...aclBody(),
          is_active: !item.is_active,
        }),
      });
      if (!res.ok) {
        let message = 'Update failed';
        try {
          const err = await res.json();
          if (err?.error) message = err.error;
        } catch { /* ignore */ }
        throw new Error(message);
      }
      await fetchManageItems(activeManageFieldKey);
      await fetchActiveLookups();
    } catch (err) {
      setManageError(err.message);
    }
  };

  const handleExportContracts = () => {
    if (!rows.length) return;
    const dataToExport = rows.map((row) => ({
      Customer: row.customer_name || '',
      'Yubiq Code': row.yubiq_code || '',
      Cluster: lookupLabel(clusterOptions, row.cluster) || row.cluster || '',
      Type: lookupLabel(typeOptions, row.tipologia_servizio) || row.tipologia_servizio || '',
      Description: row.description || '',
      'Client Manager': row.client_manager || '',
      'Service Manager': row.service_manager || '',
      'Current Monthly Fee': row.canone_mensile ?? '',
      'Renewal Monthly Fee': row.importo_rinnovo ?? '',
      'Renewal Status': lookupLabel(renewalOptions, row.stato_rinnovo) || row.stato_rinnovo || '',
      'Contract Start Date': row.inizio_contratto || '',
      'Contract End Date': row.scadenza_contratto || '',
      Note: row.note || '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contracts');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const day = new Date().toISOString().slice(0, 10);
    saveAs(blob, `contracts_${day}.xlsx`);
  };

  const renderSelectOptions = (options, currentValue) => {
    const list = [...options];
    if (currentValue && !list.some((o) => o.value === currentValue)) {
      list.unshift({ value: currentValue, label: `${currentValue} (inactive)` });
    }
    return list.map((opt) => (
      <MenuItem key={opt.value} value={opt.value}>{opt.label || opt.value}</MenuItem>
    ));
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem', mb: 0 }}>
            Contracts Management
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportContracts}
              disabled={!rows.length}
            >
              Export Excel
            </Button>
            {canManageLookups && (
              <Button
                variant="outlined"
                startIcon={<SettingsOutlinedIcon />}
                onClick={openManageDialog}
              >
                Manage Contract Values
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              color="primary"
              onClick={handleOpenDialog}
            >
              Add Contract
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              sx={{ bgcolor: 'white' }}
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Box>

      {/* Create / Edit Contract */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        scroll="body"
        sx={{
          '& .MuiDialog-container': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: 'max(28px, env(safe-area-inset-bottom, 0px))',
            paddingTop: 'max(72px, calc(env(safe-area-inset-top, 0px) + 48px))',
          },
        }}
        PaperProps={{
          sx: {
            margin: '0 auto',
            maxHeight: 'min(900px, calc(100vh - 144px))',
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ py: 2, px: 2.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <DescriptionOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {editingRow ? 'Edit contract' : 'New contract'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Manage the customer-level contract details, economic data and renewal information.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={editingRow ? 'Editing' : 'Creating'} variant="outlined" sx={{ fontWeight: 500 }} />
                  {formData.tipologia_servizio && (
                    <Chip
                      size="small"
                      label={lookupLabel(typeOptions, formData.tipologia_servizio) || formData.tipologia_servizio}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {formData.cluster && (
                    <Chip
                      size="small"
                      label={lookupLabel(clusterOptions, formData.cluster) || formData.cluster}
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
            <IconButton aria-label="close" onClick={handleCloseDialog} size="small" sx={{ color: 'text.secondary', mt: -0.5 }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5, bgcolor: 'grey.50', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Stack spacing={2.5}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)} sx={{ borderRadius: 1.5 }}>
                {formError}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <BusinessOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Customer & identification
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Customer</InputLabel>
                    <Select name="customer_id" value={formData.customer_id} label="Customer" onChange={handleFormChange}>
                      {customers.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField name="yubiq_code" label="Yubiq Code" value={formData.yubiq_code} onChange={handleFormChange} fullWidth required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Cluster</InputLabel>
                    <Select name="cluster" value={formData.cluster} label="Cluster" onChange={handleFormChange}>
                      {renderSelectOptions(clusterOptions, formData.cluster)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Type</InputLabel>
                    <Select name="tipologia_servizio" value={formData.tipologia_servizio} label="Type" onChange={handleFormChange}>
                      {renderSelectOptions(typeOptions, formData.tipologia_servizio)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <PeopleOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Contacts
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField name="client_manager" label="Client Manager" value={formData.client_manager} onChange={handleFormChange} fullWidth required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Service Manager</InputLabel>
                    <Select name="service_manager" value={formData.service_manager} label="Service Manager" onChange={handleFormChange}>
                      {serviceManagers.map((u) => (
                        <MenuItem key={u.id} value={u.username || u.email}>{u.username || u.email}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <PaymentsOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Economic data
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                {formData.tipologia_servizio === MONTHLY_RATE_VALUE && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="canone_mensile"
                      label="Current Monthly Fee (€)"
                      value={formData.canone_mensile}
                      onChange={handleFormChange}
                      type="number"
                      inputProps={{ step: '0.01' }}
                      fullWidth
                      required
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField name="importo_rinnovo" label="Renewal Monthly Fee (€)" value={formData.importo_rinnovo} onChange={handleFormChange} type="number" inputProps={{ step: '0.01' }} fullWidth required />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Renewal Status</InputLabel>
                    <Select name="stato_rinnovo" value={formData.stato_rinnovo} label="Renewal Status" onChange={handleFormChange}>
                      {renderSelectOptions(renewalOptions, formData.stato_rinnovo)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Contract period
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField name="inizio_contratto" label="Contract Start Date" value={formData.inizio_contratto} onChange={handleFormChange} type="date" InputLabelProps={{ shrink: true }} fullWidth required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField name="scadenza_contratto" label="Contract End Date" value={formData.scadenza_contratto} onChange={handleFormChange} type="date" InputLabelProps={{ shrink: true }} inputProps={{ min: formData.inizio_contratto || undefined }} fullWidth required />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <NotesOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Additional info
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField name="description" label="Description" value={formData.description} onChange={handleFormChange} multiline rows={2} fullWidth required />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="note"
                    label="Note"
                    value={formData.note}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    inputProps={{ maxLength: NOTE_MAX_LENGTH }}
                    helperText={`${formData.note.length}/${NOTE_MAX_LENGTH}`}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
          <Button onClick={handleSaveContract} variant="contained" color="primary" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Contract Values */}
      <Dialog
        open={openManage}
        onClose={closeManageDialog}
        maxWidth="md"
        fullWidth
        scroll="body"
        sx={{
          '& .MuiDialog-container': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: 'max(28px, env(safe-area-inset-bottom, 0px))',
            paddingTop: 'max(72px, calc(env(safe-area-inset-top, 0px) + 48px))',
          },
        }}
        PaperProps={{
          sx: {
            margin: '0 auto',
            maxHeight: 'min(900px, calc(100vh - 144px))',
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ py: 2, px: 2.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <SettingsOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  Manage Contract Values
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Maintain allowed values for Cluster, Type and Renewal Status. Type is also used as Service Type on projects.
                </Typography>
              </Box>
            </Stack>
            <IconButton aria-label="close" onClick={closeManageDialog} size="small" sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: 2, bgcolor: 'grey.50', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Tabs
            value={manageTab}
            onChange={(_e, v) => { setManageTab(v); setManageFormOpen(false); }}
            sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}
          >
            {LOOKUP_TABS.map((tab) => (
              <Tab key={tab.field_key} label={tab.label} />
            ))}
          </Tabs>

          {manageError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setManageError(null)}>{manageError}</Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {LOOKUP_TABS[manageTab]?.label} values
              </Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openManageCreate}>
                Add value
              </Button>
            </Stack>

            {manageLoading ? (
              <CircularProgress size={24} />
            ) : manageItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No values yet.</Typography>
            ) : (
              <Stack spacing={1}>
                {manageItems.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: item.is_active ? 'background.paper' : 'action.hover',
                      opacity: item.is_active ? 1 : 0.75,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        sort: {item.sort_order}
                        {item.value === MONTHLY_RATE_VALUE ? ' · drives Current Monthly Fee on Revenues' : ''}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={item.is_active ? 'Active' : 'Inactive'}
                      color={item.is_active ? 'success' : 'default'}
                      variant="outlined"
                    />
                    {item.value === MONTHLY_RATE_VALUE && (
                      <Chip size="small" label="Monthly Rate" color="primary" variant="outlined" />
                    )}
                    <Tooltip title="Edit label / sort">
                      <IconButton size="small" onClick={() => openManageEdit(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(item.is_active)}
                          onChange={() => handleToggleLookupActive(item)}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  </Box>
                ))}
              </Stack>
            )}

            {manageFormOpen && (
              <Box sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {manageEditing ? 'Edit value' : 'New value'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Label (display)"
                      value={manageForm.label}
                      onChange={(e) => setManageForm((p) => ({ ...p, label: e.target.value }))}
                      fullWidth
                      required
                      helperText="Shown in dropdowns. Renaming does not change existing contract records."
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Sort order"
                      type="number"
                      value={manageForm.sort_order}
                      onChange={(e) => setManageForm((p) => ({ ...p, sort_order: e.target.value }))}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button onClick={() => { setManageFormOpen(false); setManageEditing(null); }} color="inherit">
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleSaveLookup} disabled={manageSaving}>
                    {manageSaving ? <CircularProgress size={22} /> : 'Save'}
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Button onClick={closeManageDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
